// The BCP-47 tag to format dates, times and money with (#317 B38/B39).
//
// The suite ships nine UI locales (de, en, fr, es, nl, pl, sv, it, pt), and the
// backoffice pinned `'de-DE'` at ~20 `toLocaleDateString` / `toLocaleString` /
// `Intl.NumberFormat` sites. A Swedish or Portuguese recruiter therefore read
// German dates and German thousands separators throughout the ATS, in a product
// whose i18n guards enforce that every visible STRING exists in their language.
// Formatting is the half nobody was checking, and it is the half a date like
// `03.04.2026` gets wrong silently — an American reads it as March 4th.
//
// Three sites already did it right, two different ways (`locale.value || 'de-DE'`,
// `locale.value === 'de' ? 'de-DE' : 'en-US'`). This is that, once.
//
// Why a REGION is attached: a bare `'de'` leaves the region to the runtime, so the
// same build formats differently on two machines. Pinning one region per language
// makes the output a property of the user's chosen locale and nothing else.

import { watch, type Ref } from 'vue';

const REGION_BY_LANGUAGE: Record<string, string> = {
  de: 'de-DE',
  en: 'en-GB', // the suite's English is European: 24-hour clock, day-first dates
  fr: 'fr-FR',
  es: 'es-ES',
  nl: 'nl-NL',
  pl: 'pl-PL',
  sv: 'sv-SE',
  it: 'it-IT',
  pt: 'pt-PT',
};

/**
 * Map an i18n locale (`de`, `en`, … or an already-regioned tag) to the tag the
 * `Intl` formatters should use. Unknown input falls back to `de-DE`, which is what
 * every call site did unconditionally before this existed.
 */
export function uiLocale(locale?: string | null): string {
  const raw = String(locale || '').trim();
  if (!raw) return REGION_BY_LANGUAGE.de;
  if (raw.includes('-')) return raw; // already regioned — the caller was explicit
  return REGION_BY_LANGUAGE[raw.toLowerCase()] || REGION_BY_LANGUAGE.de;
}

// Issue 508: `toLocaleDateString`/`Intl.NumberFormat` READ their locale from the
// argument above, but a native `<input type="date">`/`type="datetime-local">` picker
// (component-lib ships none — every module UI uses the browser's own control) has no
// such argument. Those pickers format and order their fields off the browser/OS
// locale, and in Firefox and Safari also off the nearest ancestor's `lang` attribute
// — which every module UI's `index.html` hardcodes to `lang="de"` and never updates.
// So a Swedish recruiter planning an ATS interview got a German-ordered date field
// regardless of the locale they picked in-app. Chromium ignores `lang` for this and
// always follows the OS, so this is a partial fix there — noted, not solved, short of
// asking the OS to change (out of scope).
//
// One call per app, once i18n exists (`syncHtmlLang(i18n.global.locale)` in main.ts,
// right after `registerAppKitI18n`): sets `<html lang>` immediately and keeps it in
// step with every later locale switch. Returns the `watch` stop handle, unused by
// every current caller (an app's i18n instance lives for the tab's whole life) but
// kept so a future caller isn't forced to leak one.
export function syncHtmlLang(locale: Ref<string>): () => void {
  const apply = (value: string) => {
    if (typeof document === 'undefined' || !document.documentElement) return;
    document.documentElement.lang = value;
  };
  apply(locale.value);
  // `sync`: a locale switch is user-initiated and rare, and the pickers reading
  // `lang` do so eagerly (some re-render on the next paint) — the default batched
  // flush would leave a stale attribute for a frame.
  return watch(locale, apply, { flush: 'sync' });
}
